import { Component, OnInit } from '@angular/core'

import { MatTableDataSource } from '@angular/material/table'
import { TranslateService } from '@ngx-translate/core'
import { Subject } from 'rxjs'

import { SECURITY_TAGS } from '@@constants/security-tags'
import exportToExcel from '@@utils/exportToExcel'
import { escapeHtml } from '@@utils/buildPackageListHtml'

import { InTableColumn } from '@@shared/componenets/intable/classes/InTableColumn'
import {
    ActionId,
    InTableAction
} from '@@shared/componenets/intable/classes/InTableAction'

import { AlertService } from '@@services/alert.service'
import { BoxService } from '@@services/box.service'
import { AppService } from '@@services/app.service'

import { ConfirmationDialogsService } from '@@shared/componenets/confirmation-dialog/confirmation-dialog.service'
import { AddEditCompanyComponent } from '@@shared-dialogs/components/add-edit-company/add-edit-company.component'

@Component({
    standalone: false,
    selector: 'app-client-company-settings',
    templateUrl: './client-company-settings.component.html',
    styleUrls: ['./client-company-settings.component.scss']
})
export class ClientCompanySettingsComponent implements OnInit {
    groupBy = 'NONE'
    companies: Array<any> = []
    companiesDataSource: MatTableDataSource<any> = new MatTableDataSource<any>(
        new Array()
    )

    isInitializing: boolean = true
    companyColumns: Array<InTableColumn> = []
    companyActions: Array<InTableAction> = []

    groupByDateFormats = {
        WEEK: 'DD/MM/YYYY',
        MONTH: 'Month of DD/MM/YYYY'
    }

    searchFilter: Subject<any> = new Subject<any>()
    filterReport: any = {}

    dataInitialised = false

    groupBySelection: any = []

    SECURITY_TAGS = SECURITY_TAGS

    constructor(
        private alertService: AlertService,
        private appService: AppService,
        private boxService: BoxService,
        private translate: TranslateService,
        private confirm: ConfirmationDialogsService
    ) {}

    ngOnInit() {
        this.filterReport = {
            title: 'portal.clients.pages.client-company-settings.Report Options',
            columns: [
                {
                    title: 'portal.clients.pages.client-company-settings.Report Options',
                    formGroup: [
                        {
                            type: 'dropdown',
                            name: 'portal.clients.pages.client-company-settings.Group By',
                            formControlName: 'groupBy',
                            selection: this.groupBySelection,
                            selected: 'NONE'
                        }
                    ]
                },
                {
                    title: '',
                    formGroup: []
                },
                {
                    title: '',
                    formGroup: []
                },
                {
                    title: '',
                    formGroup: []
                }
            ],
            details: {
                columns: 1,
                closeOnOutsideClick: true,
                closeOnCancel: true,
                closeOnReset: false,
                closeOnApply: true,
                noFirstSeparator: false,
                noSecondSeparator: true,
                noThirdSeparator: true
            }
        }

        this.loadNewData()
    }

    excelExport() {
        exportToExcel(
            this.companiesDataSource,
            this.companyColumns,
            'Company Report',
            this.translate
        )
    }

    applySearch(filter: any) {
        this.companiesDataSource.filter = filter.value
            .trim()
            .toLocaleLowerCase()
        this.searchFilter.next(filter)
    }

    updateFilter(filters: any) {
        this.groupBy = filters.groupBy
        this.loadNewData()
    }

    loadNewData() {
        this.appService.getAllCompanies().subscribe((res) => {
            this.initColumns()
            this.companies = res.list
            this.companiesDataSource = new MatTableDataSource<any>(
                this.companies
            )
            this.dataInitialised = true
        })
    }

    initColumns() {
        this.companyColumns = []
        this.companyActions = []

        this.companyColumns.push(
            new InTableColumn({
                path: 'name',
                label: 'portal.clients.pages.client-company-settings.name',
                classes: 'text-underline table-cell-link',
                width: '120px'
            })
        )
        this.companyColumns.push(
            new InTableColumn({
                path: 'phoneNumber',
                label: 'portal.clients.pages.client-company-settings.phone-number',
                classes: 'hand-cursor',
                width: '120px'
            })
        )

        this.companyActions.push(
            new InTableAction(
                ActionId.EDIT,
                'portal.clients.pages.client-company-settings.edit',
                'fa fa-edit',
                undefined,
                'icon'
            )
        )
        this.companyActions.push(
            new InTableAction(
                ActionId.DELETE,
                'portal.clients.pages.client-company-settings.delete',
                'fa fa-trash',
                undefined,
                'icon'
            )
        )
    }

    tableAction(event: any) {
        if (event.action.id == ActionId.EDIT) {
            this.editCompany(event.row)
        } else if (event.action.id == ActionId.DELETE) {
            this.deleteCompany(event.row)
        }
    }

    editCompany(company: any) {
        this.boxService
            .openModal(AddEditCompanyComponent, company)
            .subscribe((saveResponse) => {
                if (saveResponse != null && saveResponse.entity) {
                    company = saveResponse.entity
                    this.companies.forEach((element: any) => {
                        if (element.id == company.id) {
                            element.name = company.name
                            element.phoneNumber = company.phoneNumber
                        }
                    })
                    this.companiesDataSource = new MatTableDataSource<any>(
                        this.companies
                    )
                }
            })
    }

    deleteCompany(company: any) {
        const key =
            'portal.clients.pages.client-company-settings.delete-confirmation'
        this.confirm
            .confirm({
                title: this.translate.instant(`${key}.title`),
                // The dialog binds its message as HTML, so the name is escaped (NB-5254).
                message: this.translate.instant(`${key}.message`, {
                    name: escapeHtml(company.name)
                }),
                btnOkText: this.translate.instant(`${key}.btnOkText`),
                btnCancelText: this.translate.instant(`${key}.btnCancelText`)
            })
            .subscribe((confirmed) => {
                if (confirmed) {
                    this.deleteCompanyConfirmed(company)
                }
            })
    }

    private deleteCompanyConfirmed(company: any) {
        this.appService.deleteCompany(company.id).subscribe((res) => {
            if (res.message) {
                this.companies = this.companies.filter((value: any) => {
                    return value.id != company.id
                })
                this.companiesDataSource = new MatTableDataSource<any>(
                    this.companies
                )
                this.alertService.success(
                    this.translate.instant(
                        'portal.clients.pages.client-company-settings.success-alert.title'
                    ),
                    this.translate.instant(
                        'portal.clients.pages.client-company-settings.success-alert.message'
                    )
                )
            }
        })
    }

    addCompany() {
        this.boxService
            .openModal(
                AddEditCompanyComponent,
                undefined,
                undefined,
                {width: '500px', height: '300px'}
            )
            .subscribe((saveResponse) => {
                if (saveResponse != null && saveResponse.entity) {
                    let company = saveResponse.entity
                    this.companies.push(company)
                    this.companiesDataSource = new MatTableDataSource(
                        this.companies
                    )
                }
            })
    }
}
